export const MAX_IMAGE_DIMENSION = 8000;
export const MAX_IMAGE_SIZE = 3_932_160; // 3.75MB
const TARGET_DIMENSION = 1568;

const MIME_TO_EXT = {"image/png": ".png", "image/jpeg": ".jpg", "image/gif": ".gif"};

export const SUPPORTED_IMAGE_MIMES = new Set(["image/png", "image/jpeg", "image/gif"]);

export const generatePastedImageName = (file, timestamp, index) => {
  const ext = file.type === "image/jpeg" ? ".jpg" : `.${file.type.split("/")[1]}`;
  return `pasted-image-${timestamp}-${index}${ext}`;
};

export const isImageFile = (fileName) => /\.(png|jpe?g|gif)$/i.test(fileName || "");

export const drawToCanvas = (img, width, height, maxDimension, needsDownscale) => {
  const scale = needsDownscale ? maxDimension / Math.max(width, height) : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error(
      "Failed to create canvas context — browser may be resource-constrained. Please close other tabs and retry.",
    );
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

export const pickBestResult = (
  file,
  blob,
  outputName,
  {oversized, needsDownscale, maxSize = MAX_IMAGE_SIZE},
) => {
  const resizedUnderLimit = blob.size <= maxSize;
  const originalUnderLimit = !oversized;

  if (resizedUnderLimit && originalUnderLimit) {
    return blob.size < file.size ? new File([blob], outputName, {type: blob.type}) : file;
  }
  if (resizedUnderLimit) {
    return new File([blob], outputName, {type: blob.type});
  }
  if (originalUnderLimit) {
    return file;
  }

  const isLossless = file.type === "image/png" || file.type === "image/gif";
  throw new Error(
    needsDownscale ?
      "Image too large even after resizing. Please reduce the resolution or save with higher compression before uploading." :
      isLossless ?
        "Image too large and re-encoding didn't reduce the file size enough. Please convert to JPEG or reduce dimensions before uploading." :
        "Image too large and re-encoding didn't reduce the file size enough. Please save with higher compression before uploading.",
  );
};

export const resizeImage = async (
  file,
  maxDimension = TARGET_DIMENSION,
  maxSize = MAX_IMAGE_SIZE,
  {skipDimensionCheck = false} = {},
) => {
  if (!file.size) {
    throw new Error("File is empty. Please select a valid image.");
  }
  const img = new Image();
  const objectUrl = URL.createObjectURL(file);
  try {
    img.src = objectUrl;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("Failed to load image"));
    });

    const {width, height} = img;

    if (!skipDimensionCheck && (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION)) {
      throw new Error(
        `Image dimensions (${width}×${height}) exceed the ${MAX_IMAGE_DIMENSION}px limit. Please resize before uploading.`,
      );
    }

    const needsDownscale = width > maxDimension || height > maxDimension;
    const oversized = file.size > maxSize;

    if (file.type === "image/gif") {
      if (oversized) {
        throw new Error(
          `This GIF is too large (max ${Math.round((maxSize / 1024 / 1024) * 100) / 100}MB). Please use a shorter or smaller GIF.`,
        );
      }
      return file;
    }

    if (!needsDownscale && !oversized) {
      return file;
    }
    if (needsDownscale && !oversized && file.size <= 500_000) {
      return file;
    }

    const canvas = drawToCanvas(img, width, height, maxDimension, needsDownscale);

    let blob = await new Promise((resolve) => canvas.toBlob(resolve, file.type, 0.85));
    let outputName = file.name;
    let usedFallbackFormat = false;

    if (!blob) {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      usedFallbackFormat = true;
    }

    if (!blob) {
      if (!oversized) {
        return file;
      }
      throw new Error(
        "Image too large and couldn't be resized automatically. Please resize to under 3.75MB manually.",
      );
    }

    if (usedFallbackFormat) {
      const newExt = MIME_TO_EXT[blob.type] || ".png";
      outputName = file.name.replace(/\.[^.]+$/, "") + newExt;
    }

    return pickBestResult(file, blob, outputName, {oversized, needsDownscale, maxSize});
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
